import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import FeaturedProducts from './components/FeaturedProducts';
import CategorySection from './components/CategorySection';
import ProductShowcase from './components/ProductShowcase';
import Reviews from './components/Reviews';
import ContactUs from './components/ContactUs';
import Footer from './components/Footer';
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import WhatnotLink from './components/WhatnotLink';
import Authenticity from './components/Authenticity';
import { FiArrowUp } from 'react-icons/fi';
import ProductDetail from './pages/ProductDetail';
import CategoryPage from './pages/CategoryPage';
import AllGrailsPage from './pages/AllGrailsPage';
import EntireInventoryPage from './pages/EntireInventoryPage';
import TeaserPage from './pages/TeaserPage';
import ScrollToTop from './components/ScrollToTop';
import { CartProvider } from './contexts/CartContext';
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';

// Section divider component
const SectionDivider = () => (
  <div className="relative py-3 flex justify-center">
    <div className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent"></div>
    <div className="absolute w-6 h-6 rounded-full bg-background-dark border border-gray-800/70 flex items-center justify-center z-10">
      <div className="w-1 h-1 bg-accent/80 rounded-full"></div>
    </div>
  </div>
);

// Back to top button component
const BackToTopButton = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  
  const toggleVisibility = () => {
    if (window.pageYOffset > 500) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };
  
  const scrollToTop = () => {
    // Use instant scroll for this button
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Smooth animation still provides a better UX
    });
  };
  
  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);
  
  return (
    <button 
      onClick={scrollToTop}
      className={`fixed bottom-4 md:bottom-8 right-4 md:right-8 p-3 rounded-full bg-accent/90 text-white shadow-lg hover:bg-accent hover:scale-110 transition-all duration-300 z-50 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
      }`}
      aria-label="Back to top"
    >
      <FiArrowUp className="w-5 h-5" />
    </button>
  );
};

// HomePage component for the main store front
const HomePage = () => {
  // Force scroll to top on initial page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-dark text-primary">
      <Header />
      <main className="relative">
        {/* Background gradient overlay that follows scroll */}
        <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-purple-900/5 via-background-dark to-background-dark opacity-40"></div>
        
        {/* Hero section */}
        <section id="hero" className="relative">
          <Hero />
        </section>
        
        <div className="relative z-10">
          {/* Content container with consistent padding and max width */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Whatnot Stream Section */}
            <section id="whatnot" className="py-10">
              <WhatnotLink />
            </section>
            
            <SectionDivider />
            
            {/* Featured Products Section */}
            <section id="featured" className="py-10">
              <FeaturedProducts />
            </section>
            
            <SectionDivider />
            
            {/* Category Section with decorative elements */}
            <section className="py-10 relative overflow-hidden" id="categories">
              <div className="absolute -left-64 top-1/4 w-96 h-96 bg-accent/5 rounded-full filter blur-3xl opacity-30 animate-blob"></div>
              <div className="absolute -right-64 bottom-1/4 w-96 h-96 bg-purple-800/5 rounded-full filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
              <CategorySection />
            </section>
            
            <SectionDivider />
            
            {/* Product Showcase Section */}
            <section className="py-10" id="inventory">
              <ProductShowcase />
            </section>
            
            <SectionDivider />
            
            {/* Authenticity Section */}
            <section className="py-10" id="authenticity">
              <Authenticity />
            </section>
            
            <SectionDivider />
            
            {/* Reviews Section */}
            <section className="py-10" id="reviews">
              <Reviews />
            </section>
            
            <SectionDivider />
            
            {/* Contact Section */}
            <section className="py-10" id="contact">
              <ContactUs />
            </section>
          </div>
        </div>
        
        {/* Back to top button */}
        <BackToTopButton />
      </main>
      <Footer />
    </div>
  );
};

// Secret admin access path - should be stored in environment variables
const ADMIN_ACCESS_PATH = process.env.REACT_APP_ADMIN_PATH || 'secure-admin-route';

// Flag to control whether to show teaser page or main site
// You can change this to false when you're ready to show the main site
const SHOW_TEASER_PAGE = false;

function Main() {
  return (
    <Router>
      <CartProvider>
        <ScrollToTop />
        <Routes>
          {/* Main route - conditionally renders TeaserPage or HomePage based on SHOW_TEASER_PAGE flag */}
          <Route path="/" element={SHOW_TEASER_PAGE ? <TeaserPage /> : <HomePage />} />
          
          {/* Admin routes - these will always work regardless of teaser mode */}
          <Route path={`/${ADMIN_ACCESS_PATH}/login`} element={<AdminLogin />} />
          <Route 
            path={`/${ADMIN_ACCESS_PATH}/dashboard`} 
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Redirect /admin to dashboard if authenticated, otherwise to home */}
          <Route 
            path="/admin" 
            element={
              localStorage.getItem('adminAuth') === 'true' 
                ? <Navigate to={`/${ADMIN_ACCESS_PATH}/dashboard`} replace /> 
                : <Navigate to="/" replace />
            } 
          />
          
          {/* Redirect old admin paths to home */}
          <Route path="/admin/login" element={<Navigate to="/" replace />} />
          <Route path="/admin/dashboard" element={<Navigate to="/" replace />} />
          
          {/* Main store routes - only accessible if SHOW_TEASER_PAGE is false */}
          {!SHOW_TEASER_PAGE && (
            <>
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/category/:id" element={<CategoryPage />} />
              <Route path="/all-heat" element={<AllGrailsPage />} />
              <Route path="/inventory" element={<EntireInventoryPage />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
            </>
          )}
          
          {/* If SHOW_TEASER_PAGE is true, redirect all product/category pages to home */}
          {SHOW_TEASER_PAGE && (
            <>
              <Route path="/product/:id" element={<Navigate to="/" replace />} />
              <Route path="/category/:id" element={<Navigate to="/" replace />} />
              <Route path="/all-heat" element={<Navigate to="/" replace />} />
              <Route path="/inventory" element={<Navigate to="/" replace />} />
              <Route path="/checkout" element={<Navigate to="/" replace />} />
              <Route path="/checkout/success" element={<Navigate to="/" replace />} />
            </>
          )}
          
          {/* Keep a direct path to the teaser page */}
          <Route path="/teaser" element={<TeaserPage />} />
          
          {/* Catch-all redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </CartProvider>
    </Router>
  );
}

export default Main; 